import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  Monitor,
  Shield,
  Cloud,
  Lightbulb,
  Check,
  Award,
  Clock,
  Handshake
} from "lucide-react";

const ServicesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConsultation = () => {
    navigate("/#contact");
  };

  return (
    <div className="min-h-screen" data-testid="services-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header Section */}
        <section
          data-testid="services-header"
          className="py-16 md:py-20 px-5"
          style={{ backgroundColor: "#F9FAFB" }}
        >
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <nav
              data-testid="breadcrumb"
              className="mb-5 text-sm"
              style={{ color: "#6B7280" }}
            >
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <span>Services</span>
            </nav>
            
            <h1
              data-testid="services-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: "#1F2937" }}
            >
              Complete IT Solutions for Your Business
            </h1>
            <p
              data-testid="services-subtitle"
              className="text-lg"
              style={{ color: "#4A5568", maxWidth: "800px", margin: "0 auto" }}
            >
              Comprehensive IT services designed to keep your business running smoothly, securely, and efficiently
            </p>
          </div>
        </section>

        {/* Service Overview Grid */}
        <section
          data-testid="services-grid"
          className="py-20 px-5 bg-white"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Service Card 1 */}
              <div
                data-testid="main-service-0"
                className="bg-white border border-gray-200 rounded-xl p-10 md:p-12 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
              >
                <Monitor size={56} style={{ color: "#3B82F6" }} className="mb-6" />
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: "#1F2937" }}>
                  Managed IT Services
                </h2>
                <p className="text-base mb-6 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Comprehensive IT support with 24/7 monitoring, help desk, and proactive maintenance to keep your business running smoothly
                </p>
                <Link
                  to="/services/managed-it"
                  className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: "#3B82F6" }}
                  data-testid="service-btn-0"
                >
                  Learn More
                </Link>
              </div>

              {/* Service Card 2 */}
              <div
                data-testid="main-service-1"
                className="bg-white border border-gray-200 rounded-xl p-10 md:p-12 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
              >
                <Shield size={56} style={{ color: "#2C5F2D" }} className="mb-6" />
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: "#1F2937" }}>
                  Cybersecurity
                </h2>
                <p className="text-base mb-6 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Protect your business from cyber threats with enterprise-grade security solutions, monitoring, and employee training
                </p>
                <Link
                  to="/services/cybersecurity"
                  className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: "#3B82F6" }}
                  data-testid="service-btn-1"
                >
                  Learn More
                </Link>
              </div>

              {/* Service Card 3 */}
              <div
                data-testid="main-service-2"
                className="bg-white border border-gray-200 rounded-xl p-10 md:p-12 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
              >
                <Cloud size={56} style={{ color: "#3B82F6" }} className="mb-6" />
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: "#1F2937" }}>
                  Cloud Solutions
                </h2>
                <p className="text-base mb-6 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Flexible cloud services including Microsoft 365, cloud backup, and infrastructure to enable work from anywhere
                </p>
                <Link
                  to="/services/cloud-solutions"
                  className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: "#3B82F6" }}
                  data-testid="service-btn-2"
                >
                  Learn More
                </Link>
              </div>

              {/* Service Card 4 */}
              <div
                data-testid="main-service-3"
                className="bg-white border border-gray-200 rounded-xl p-10 md:p-12 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
              >
                <Lightbulb size={56} style={{ color: "#2C5F2D" }} className="mb-6" />
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: "#1F2937" }}>
                  IT Consulting & Strategy
                </h2>
                <p className="text-base mb-6 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Strategic technology guidance to align IT with your business goals, plan upgrades, and optimize your infrastructure
                </p>
                <Link
                  to="/services/it-consulting"
                  className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: "#3B82F6" }}
                  data-testid="service-btn-3"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* All Services List Section */}
        <section
          data-testid="all-services-section"
          className="py-20 px-5"
          style={{ backgroundColor: "#F9FAFB" }}
        >
          <div className="max-w-5xl mx-auto">
            <h2
              data-testid="all-services-title"
              className="text-3xl sm:text-4xl font-bold text-center mb-16"
              style={{ color: "#1F2937" }}
            >
              Our Complete Service Offerings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0 max-w-4xl mx-auto">
              {/* Column 1 */}
              <div>
                <ServiceItem text="24/7 Network Monitoring" />
                <ServiceItem text="Help Desk Support" />
                <ServiceItem text="Server Management" />
                <ServiceItem text="Backup & Disaster Recovery" />
                <ServiceItem text="Email & Microsoft 365" />
                <ServiceItem text="Hardware Procurement" />
              </div>
              {/* Column 2 */}
              <div>
                <ServiceItem text="Network Security" />
                <ServiceItem text="Firewall Management" />
                <ServiceItem text="Antivirus & Anti-malware" />
                <ServiceItem text="Security Audits" />
                <ServiceItem text="Employee Training" />
                <ServiceItem text="Compliance Support" />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          data-testid="services-cta"
          className="py-20 px-5 bg-white"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-2xl sm:text-3xl font-bold mb-4"
              style={{ color: "#1F2937" }}
            >
              Not Sure What You Need?
            </h2>
            <p
              className="text-lg mb-8"
              style={{ color: "#4A5568" }}
            >
              We'll help you find the right solution for your business
            </p>
            <Button
              onClick={handleConsultation}
              data-testid="cta-consultation-btn"
              className="text-lg font-bold px-10 py-6 rounded-lg shadow-md hover:shadow-lg transition-all"
              style={{ backgroundColor: "#3B82F6" }}
            >
              Schedule a Free Consultation
            </Button>
            <p className="mt-4 text-base" style={{ color: "#4A5568" }}>
              Or call us at{" "}
              <a
                href="tel:7783497449"
                className="font-semibold hover:underline"
                style={{ color: "#3B82F6" }}
                data-testid="cta-phone"
              >
                (778) 349-7449
              </a>
            </p>
          </div>
        </section>

        {/* Why Choose Section */}
        <section
          data-testid="why-choose-services"
          className="py-20 px-5"
          style={{ backgroundColor: "#F9FAFB" }}
        >
          <div className="max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-bold text-center mb-16"
              style={{ color: "#1F2937" }}
            >
              Why Choose A3 Technologies?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Benefit 1 */}
              <div
                data-testid="benefit-card-0"
                className="bg-white rounded-lg p-10 text-center"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
              >
                <Award size={48} style={{ color: "#3B82F6" }} className="mx-auto mb-5" />
                <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>
                  Experienced Professionals
                </h3>
                <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>
                  Certified IT experts with years of experience supporting businesses like yours
                </p>
              </div>

              {/* Benefit 2 */}
              <div
                data-testid="benefit-card-1"
                className="bg-white rounded-lg p-10 text-center"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
              >
                <Clock size={48} style={{ color: "#2C5F2D" }} className="mx-auto mb-5" />
                <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>
                  Fast Response Times
                </h3>
                <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>
                  Quick response to your IT issues ensures minimal downtime and maximum productivity
                </p>
              </div>

              {/* Benefit 3 */}
              <div
                data-testid="benefit-card-2"
                className="bg-white rounded-lg p-10 text-center"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
              >
                <Handshake size={48} style={{ color: "#3B82F6" }} className="mx-auto mb-5" />
                <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>
                  Proactive Approach
                </h3>
                <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>
                  We prevent problems before they happen through monitoring and regular maintenance
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Service Item Component
const ServiceItem = ({ text }) => (
  <div className="flex items-center gap-4 py-3 px-4 rounded-md hover:bg-gray-100 transition-colors">
    <Check size={20} style={{ color: "#2C5F2D", flexShrink: 0 }} />
    <span className="text-lg" style={{ color: "#374151" }}>{text}</span>
  </div>
);

export default ServicesPage;
